//External
import React, { useEffect, useState } from 'react';
import { Transition } from '@headlessui/react';
import * as _ from 'lodash';
import { gql, useLazyQuery, useMutation } from '@apollo/client';
import { Link } from 'react-router-dom';
import { ExclamationCircleIcon, PlusSmIcon } from '@heroicons/react/solid';

// Internal
import { ListingDetails_ListingData_listing } from '../../../types/graph/ListingDetails_ListingData';
import { ListingContacts_ContactSearchData_contacts } from '../../../types/graph/ListingContacts_ContactSearchData';
import paths from '../../../constants/routes.json';
import { PlusIcon } from '@heroicons/react/outline';

// Component interface
interface IProps {
  listing: ListingDetails_ListingData_listing;
}

// Form data validation schema

const Contacts: React.FC<IProps> = ({ listing }) => {
  /**
   * States
   */

  const [searchFilter, setSearchFilter] = useState<string>('');
  const [searchResults, setSearchResults] = useState<
    ListingContacts_ContactSearchData_contacts[]
  >([]);
  const [showDropdown, setShowDropdown] = useState<boolean>(false);
  const [selectedContact, setSelectedContact] =
    useState<ListingContacts_ContactSearchData_contacts | null>(null);
  const [searching, setSearching] = useState<boolean>(false);

  // Graph Query - Search Contact data
  const [
    searchContacts,
    { called, loading, data, refetch: refetchSearchContacts },
  ] = useLazyQuery(CONTACT_SEARCH, {
    onCompleted: ({ contacts }) => {
      setTimeout(() => {
        setSearching(false);
      }, 500);

      setSearchResults(contacts);
    },
    fetchPolicy: 'network-only',
  });
  // Graph Mutation - Update Listing
  const [updateListing] = useMutation(UPDATE_LISTING, {
    onCompleted: () => {
      setSearchFilter('');
      //setSearchResults([]);
      setShowDropdown(false);
    },
  });

  useEffect(() => {
    //setShowDropdown(searchResults.length <= 0 ? false : true);
    //console.log(searchResults);
  }, [searchResults]);

  const onSearchInput = _.debounce((filter: string) => {
    if (filter.length >= 1) {
      //console.log('SEARCH CALLED');
      setSearching(true);
      searchContacts({ variables: { filter } });
    } else {
      //setSearchResults([]);
    }
  }, 800);

  const selectSuggestion = (contact: any) => {
    setSelectedContact(contact);
    setSearchFilter(`
      ${contact.person.name.full} 
      (${contact.person.email})
      ${contact.company.name}
      ${contact.company.title}`);
    //setSearchResults([]);
    setShowDropdown(false);
    onAddContactToListing(contact);
  };

  /**
   * Handlers
   */
  const onAddContactToListing = (contact: any) => {
    // if (selectedContact?.id)
    //   updateListing({
    //     variables: {
    //       listingId: listing.id,
    //       listing: {
    //         contacts: [selectedContact!.id],
    //       },
    //     },
    //   });

    updateListing({
      variables: {
        listingId: listing.id,
        listing: {
          contacts: [contact!.id],
        },
      },
    });
  };

  const forceSearch = (filter: string) => {
    setSearching(true);
    searchContacts({ variables: { filter } });
    // if (refetchSearchContacts) {
    //   refetchSearchContacts({ variables: { filter } });
    // }
  };

  return (
    <div>
      <div className="space-y-1 border-b border-gray-200 pb-5">
        <label
          htmlFor="add_team_members"
          className="block text-sm font-medium text-gray-700"
        >
          Add a Contact to your Listing
        </label>
        <div className="flex space-x-3 relative overflow-visible h-full">
          <div className="relative w-1/3 flex flex-col overflow-visible">
            <input
              type="text"
              name="add_team_members"
              id="add_team_members"
              className="block w-full shadow-sm focus:ring-primary-light focus:border-primary-light sm:text-sm border-gray-300 rounded-md"
              placeholder="Search/Select for contact..."
              aria-describedby="add_team_members_helper"
              autoComplete="off"
              value={searchFilter}
              onFocus={(e) => {
                setSearchFilter(e.target.value);
                forceSearch(e.target.value);
                setShowDropdown(true);
              }}
              onChange={(e) => {
                setSearchFilter(e.target.value);
                onSearchInput(e.target.value);
              }}
              onBlur={() => setShowDropdown(false)}
            />

            {searching && (
              <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                <svg
                  className="animate-spin mx-auto h-5 w-5 text-gray-400"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx={12}
                    cy={12}
                    r={10}
                    stroke="currentColor"
                    strokeWidth={4}
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
              </div>
            )}

            <Transition
              as={'div'}
              show={showDropdown}
              leave="transition ease-in duration-100"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
              className="max-h-80 overflow-auto w-full absolute top-11 rounded-md bg-white shadow-lg"
            >
              <ul
                tabIndex={-1}
                className="h-full rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm"
              >
                <li
                  className="text-gray-500 hover:bg-gray-200 cursor-pointer select-none relative mx-2 py-2 pr-4 border border-gray-300 rounded-lg mb-2"
                  onClick={() => {}}
                >
                  <div className="w-full flex items-center justify-center">
                    <Link to={`${paths.PEOPLE}/new/${listing.id}`}>
                      <div className="flex">
                        <PlusIcon className="w-5 h-5 mr-2" />
                        Add New Contact
                      </div>
                    </Link>
                  </div>
                </li>

                {searchResults.map((contact, i) => (
                  <li
                    key={i}
                    className="text-gray-900 hover:bg-gray-200 cursor-pointer select-none relative py-2 pl-12 pr-4"
                    onClick={() => onAddContactToListing(contact)}
                  >
                    <span className="font-normal block truncate">
                      {`${contact.person?.name!.full}`}
                    </span>
                    <span className="text-sm text-gray-500 font-normal block truncate">
                      {`${contact.person?.email}`}
                    </span>
                    <span className="absolute inset-y-0 left-0 flex items-center pl-1.5">
                      {/* Heroicon name: solid/location-marker */}
                      <svg
                        className="h-8 w-8 rounded-full text-gray-300"
                        fill="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path d="M24 20.993V24H0v-2.996A14.977 14.977 0 0112.004 15c4.904 0 9.26 2.354 11.996 5.993zM16.002 8.999a4 4 0 11-8 0 4 4 0 018 0z" />
                      </svg>
                    </span>
                  </li>
                ))}
                {/* {searching && (
                  <li className="py-2">
                    <svg
                      className="animate-spin mx-auto h-5 w-5 text-gray-400"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx={12}
                        cy={12}
                        r={10}
                        stroke="currentColor"
                        strokeWidth={4}
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                  </li>
                )} */}
              </ul>
            </Transition>
          </div>

          {/* <div className="">
            <Button
              variant="secondary"
              type="button"
              onClick={onAddContactToListing}
              disabled={
                searchFilter === '' ||
                searchFilter === null ||
                searchFilter === undefined
              }
            >
              <PlusSmIcon className="-ml-2 mr-1 h-5 w-5 text-gray-400" />
              <span>Add</span>
            </Button>
          </div> */}

          {/* <div className="w-full flex justify-end">
            <Link
              to={`${paths.PEOPLE}/new/${listing.id}`}
              type="button"
              className="bg-white inline-flex items-center object-right px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-light"
            >
              Create and Add New Contact
            </Link>
          </div> */}
        </div>
      </div>
      {/* ContactList */}
      <div className="mt-4 bg-white shadow overflow-hidden sm:rounded-md">
        <ul className="divide-y divide-gray-200">
          {listing.contacts &&
            listing.contacts.map((contact) => (
              <li key={contact!.id}>
                <div className="flex items-center px-8 py-4 sm:px-8">
                  <div className="w-full flex items-center">
                    {/* Agent */}
                    <div className="flex flex-grow">
                      <div className="flex-shrink-0">
                        {_.get(contact, 'person.avatar.filePath', false) ? (
                          <img
                            className="h-12 w-12 rounded-full"
                            src={_.get(contact, 'person.avatar.filePath', '')}
                            alt="Headshot"
                          />
                        ) : (
                          <svg
                            className="h-12 w-12 rounded-full text-gray-300"
                            fill="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path d="M24 20.993V24H0v-2.996A14.977 14.977 0 0112.004 15c4.904 0 9.26 2.354 11.996 5.993zM16.002 8.999a4 4 0 11-8 0 4 4 0 018 0z" />
                          </svg>
                        )}
                      </div>
                      <div className="ml-6 flex flex-col">
                        <p className="text-sm font-medium text-primary-dark truncate">
                          {`${_.get(contact, 'person.name.full', '')}`}
                        </p>
                        <p className="mt-2 flex items-center text-sm text-gray-500">
                          {/* Heroicon name: solid/mail */}
                          <svg
                            className="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400"
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                            aria-hidden="true"
                          >
                            <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                            <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                          </svg>
                          <span className="truncate">
                            {_.get(contact, 'person.email', '')}
                          </span>
                        </p>
                      </div>
                    </div>
                    {/* Company */}
                    <div className="flex flex-grow">
                      <div className="flex-shrink-0">
                        {_.get(contact, 'company.logo.filePath', false) ? (
                          <img
                            className="h-11"
                            src={_.get(contact, 'company.logo.filePath', '')}
                            alt="Logo"
                          />
                        ) : (
                          <svg
                            className="h-12 w-12 text-gray-300"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                            />
                          </svg>
                        )}
                      </div>
                      <div className="ml-6 flex flex-col">
                        <p className="text-sm font-medium text-primary-dark truncate">
                          {_.get(contact, 'company.name', '')}
                        </p>
                        <p className="mt-2 flex items-center text-sm text-gray-500">
                          {/* Heroicon name: solid/mail */}
                          <svg
                            className="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400"
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                          >
                            <path
                              fillRule="evenodd"
                              d="M6 6V5a3 3 0 013-3h2a3 3 0 013 3v1h2a2 2 0 012 2v3.57A22.952 22.952 0 0110 13a22.95 22.95 0 01-8-1.43V8a2 2 0 012-2h2zm2-1a1 1 0 011-1h2a1 1 0 011 1v1H8V5zm1 5a1 1 0 011-1h.01a1 1 0 110 2H10a1 1 0 01-1-1z"
                              clipRule="evenodd"
                            />
                            <path d="M2 13.692V16a2 2 0 002 2h12a2 2 0 002-2v-2.308A24.974 24.974 0 0110 15c-2.796 0-5.487-.46-8-1.308z" />
                          </svg>
                          <span className="truncate">
                            {_.get(contact, 'company.title', '')}
                          </span>
                        </p>
                      </div>
                    </div>
                    {/* Controls */}
                    <div className="mr-10 flex flex-grow space-x-5 justify-end">
                      <button
                        type="button"
                        className="bg-white inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-light"
                        onClick={() => {
                          updateListing({
                            variables: {
                              listingId: listing.id,
                              listing: {
                                contactsRemove: [contact!.id],
                              },
                            },
                          });
                        }}
                      >
                        {/* Heroicon name: solid/plus */}
                        <svg
                          className="-ml-2 mr-1 h-5 w-5 text-gray-400"
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M3 10a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z"
                            clipRule="evenodd"
                          />
                        </svg>
                        <span>Remove</span>
                      </button>
                      <Link
                        to={`${paths.PEOPLE}/${contact!.id}/${listing.id}`}
                        type="button"
                        className="bg-white inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-light"
                      >
                        {/* Heroicon name: solid/plus */}
                        <svg
                          className="-mx-2 h-5 w-5 text-gray-400"
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                        </svg>
                      </Link>
                    </div>
                  </div>
                  {/* <div>
                      <svg
                        className="h-5 w-5 text-gray-400"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                        aria-hidden="true"
                      >
                        <path
                          fillRule="evenodd"
                          d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div> */}
                </div>
              </li>
            ))}
        </ul>
      </div>
    </div>
  );
};

export default Contacts;

/**
 * TODO: Create/Move to /Account/Account.graph.ts (Maybe - R&D required for graph queries strategy)
 */
const CONTACT_SEARCH = gql`
  query ListingContacts_ContactSearchData($filter: String) {
    contacts(filter: $filter) {
      id
      person {
        name {
          first
          last
          full
        }
        email
        phones {
          number
          type
        }
        socialLinks {
          url
          type
        }
        avatar {
          filePath
        }
      }
      company {
        name
        title
        socialLinks {
          url
          type
        }
        logo {
          filePath
        }
      }
    }
  }
`;

export const UPDATE_LISTING = gql`
  mutation ListingContacts_UpdateListingData(
    $listingId: String!
    $listing: ListingInput!
  ) {
    listingUpdate(listingId: $listingId, listing: $listing) {
      id
      property {
        id
      }
      contacts {
        id
        person {
          name {
            first
            last
            full
          }
          email
          phones {
            number
            type
          }
          socialLinks {
            url
            type
          }
          avatar {
            filePath
          }
        }
        company {
          name
          title
          socialLinks {
            url
            type
          }
          logo {
            filePath
          }
        }
      }
    }
  }
`;

//////
// Abstraction of components created during Profile JSX cleanup
//
/**
 * TODO: Create/Move to /components/InputGroup.tsx and import
 */
const InputGroup = ({
  label,
  children,
  ...props
}: {
  label: string;
  children: React.ReactNode;
}) => (
  <div>
    <label {...props} className="block text-sm font-medium text-gray-700">
      {label}
    </label>
    {children}
  </div>
);
/**
 * TODO: Create/Move to /components/InputField.tsx and import
 */
const InputField = React.forwardRef<
  HTMLInputElement,
  {
    error?: any;
    [x: string]: any;
  }
>(({ error, ...props }, ref) => (
  <div className="relative">
    <input
      ref={ref}
      {...props}
      className={`${
        error
          ? `border-red-300 placeholder-red-300 focus:ring-red-500 focus:border-red-500`
          : `border-gray-300 placeholder-gray-400 focus:ring-primary-light focus:border-primary-light`
      } appearance-none block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none sm:text-sm`}
    />
    {error ? (
      <div className="absolute inset-y-0 -top-7 right-0 pr-3 flex items-center pointer-events-none">
        <ExclamationCircleIcon className="h-5 w-5 text-red-500" />
      </div>
    ) : null}
    <p className="mt-2 text-sm text-red-600">{error?.message}</p>
  </div>
));
/**
 * TODO: Create/Move to /components/Button.tsx and import
 */
// MB comment: Based on Mark's button as per Profile.tsx
// Enhancements: added the "disabled" attribute with disabled logic and associated UI feedbacks
// Currently, only variant=secondary is fully tested, the rest of variants are not tested yet.
const Button = ({
  variant,
  type = 'button',
  disabled = false,
  children,
  className = '',
  onClick,
}: {
  variant: string;
  type: 'button' | 'submit';
  disabled?: boolean;
  children: React.ReactNode;
  className?: string;
  onClick?: any;
}) => {
  return variant === 'primary' ? (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={`${
        disabled
          ? 'cursor-not-allowed hover:bg-primary'
          : 'hover:bg-primary-dark'
      }  bg-primary border border-transparent rounded-md shadow-sm py-2 px-4 inline-flex justify-center text-sm font-medium text-white hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-light ${className}`}
    >
      {children}
    </button>
  ) : variant === 'secondary' ? (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={`${
        disabled ? 'cursor-not-allowed hover:bg-white ' : 'hover:bg-gray-50'
      }  bg-white inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-light ${className}`}
    >
      {children}
    </button>
  ) : variant === 'primary icon' ? (
    <button
      type={type}
      className={`${
        disabled
          ? 'cursor-not-allowed hover:bg-primary'
          : 'hover:bg-primary-dark'
      }  bg-primary border border-transparent rounded-md shadow-sm py-2 px-4 inline-flex justify-center text-sm font-medium text-white hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-light ${className}`}
      onClick={onClick}
      disabled={disabled}
    >
      {children}
    </button>
  ) : variant === 'secondary icon' ? (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={`${
        disabled ? 'cursor-not-allowed hover:bg-white' : 'hover:bg-gray-50'
      }  bg-white inline-flex items-center px-2 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-light ${className}`}
    >
      {children}
    </button>
  ) : null;
};
